import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import CatalogHomepageTab from '@/components/Collection/CatalogHomepageTab';
import CatalogMasterHomepageTab from '@/components/Collection/CatalogMasterHomepageTab';
import CollectionPagesTab from '@/components/Collection/CollectionPagesTab';
import Header from '@/components/Collection/Header';
import NewItemsCollectionCallout from '@/components/Collection/NewItemsCollectionCallout';
import PastHomepageCollectionsTab from '@/components/Collection/PastHomepageCollectionsTab';
import Tabs, { Tab } from '@/components/Tabs';
import { useGetWarehousesQuery } from '@/graphql/types/generated';
import { FeatureFlag, useFeatureFlipper } from '@/hooks/featureflipper';
import TwoColLayout from '@/layouts/two-col';

const tabs: Tab[] = [
  { id: 'catalog-homepage', label: 'Catalog Homepage' },
  { id: 'past-homepage', label: 'Past Homepage Collections' },
  { id: 'collection-pages', label: 'Collection Page' },
];

export default function CollectionsIndex() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [{ data: warehousesData }] = useGetWarehousesQuery();
  const isSelfServiceMarketingCollections = useFeatureFlipper(
    FeatureFlag.selfServiceMarketingCollections,
  );
  const isSelfServiceMasterHomepages = useFeatureFlipper(
    FeatureFlag.selfServiceMasterHomepages,
  );
  const isNewItemPinningEnabled = useFeatureFlipper(FeatureFlag.NewItemPinning);
  const availableTabs = isSelfServiceMasterHomepages
    ? tabs.filter((tab) => tab.id !== 'past-homepage')
    : tabs;

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (!tabFromUrl || !availableTabs.some((tab) => tab.id === tabFromUrl)) {
      setSearchParams({ tab: 'catalog-homepage' }, { replace: true });
    }
  }, [availableTabs, searchParams, setSearchParams]);

  if (isSelfServiceMarketingCollections === undefined) {
    return null;
  }
  if (!isSelfServiceMarketingCollections) {
    return <Navigate to="/" replace />;
  }

  const totalWarehouses = warehousesData?.warehouses?.length || 0;
  const activeTab = searchParams.get('tab') || 'catalog-homepage';

  return (
    <TwoColLayout>
      <Header showAddCollectionToHomepage={!isSelfServiceMasterHomepages} />
      <Tabs
        tabs={availableTabs}
        activeTab={activeTab}
        onTabChange={(tabId) =>
          setSearchParams({ tab: tabId }, { replace: true })
        }
      >
        {activeTab === 'catalog-homepage' &&
          (isSelfServiceMasterHomepages ? (
            <CatalogMasterHomepageTab
              totalWarehouses={totalWarehouses}
              warehouses={warehousesData?.warehouses ?? []}
            />
          ) : (
            <CatalogHomepageTab
              totalWarehouses={totalWarehouses}
              warehouses={warehousesData?.warehouses ?? []}
            />
          ))}
        {activeTab === 'past-homepage' && (
          <PastHomepageCollectionsTab totalWarehouses={totalWarehouses} />
        )}
        {activeTab === 'collection-pages' && (
          <>
            {isNewItemPinningEnabled && <NewItemsCollectionCallout />}
            <CollectionPagesTab totalWarehouses={totalWarehouses} />
          </>
        )}
      </Tabs>
    </TwoColLayout>
  );
}
